/**
 * Người mua CÁ NHÂN của hoá đơn eShop (CEO 24/10/2026, migration 0609).
 *
 * Nghị định 254/2026: khách không đưa thông tin thì hoá đơn ghi «Bán cho người
 * tiêu dùng»; đưa thì ghi tên thật (+ địa chỉ, số định danh). Tên khách trong hệ
 * là nick mạng xã hội — KHÔNG BAO GIỜ thành tên người mua.
 *
 * Luật kiểm ở đây là bản giao diện của `invoicebuyer` + `normalizePersonalBuyer`
 * bên máy chủ (máy chủ vẫn kiểm cuối). Giữ cùng khuôn: CCCD đúng 12 chữ số, hộ
 * chiếu 6–20 chữ/số kèm quốc tịch.
 */
import type { CustomerBookingInvoiceType } from "./api";

/** Chữ pháp lý — đúng từng ký tự. */
export const CONSUMER_BUYER_NAME = "Bán cho người tiêu dùng";

export type PersonIdKind = "" | "cccd" | "passport";

export type PersonBuyer = {
  /** false = ô «Khách không cung cấp thông tin xuất hoá đơn» đang tích. */
  provided: boolean;
  name: string;
  address: string;
  idKind: PersonIdKind;
  idNumber: string;
  idNationality: string;
};

/** Mặc định: khách không cung cấp (CEO 4b — chọn hay không là tuỳ, bỏ trống = không cung cấp). */
export const emptyPersonBuyer: PersonBuyer = {
  provided: false,
  name: "",
  address: "",
  idKind: "",
  idNumber: "",
  idNationality: "",
};

export type PersonFieldErrors = Partial<
  Record<"name" | "address" | "idNumber" | "idNationality", string>
>;

const cccdPattern = /^[0-9]{12}$/;
const passportPattern = /^[A-Z0-9]{6,20}$/;

/** Số đã che (vai không đủ quyền nhận `••••••••1234`) — gửi lại = không đổi. */
export function isMaskedIdNumber(value: string): boolean {
  return value.includes("•");
}

export function normalizeIdNumber(value: string): string {
  return value.replace(/\s+/g, "").toUpperCase();
}

export function validatePersonBuyer(
  invoiceType: CustomerBookingInvoiceType,
  person: PersonBuyer,
): PersonFieldErrors {
  if (invoiceType !== "personal" || !person.provided) return {};
  const errors: PersonFieldErrors = {};
  const name = person.name.trim();
  if (!name) errors.name = "Nhập tên thật của người mua (không phải nick)";
  else if (name.toLowerCase() === CONSUMER_BUYER_NAME.toLowerCase())
    errors.name = "Khách không cung cấp thì tích ô bên trên, đừng gõ vào ô tên";
  else if ([...name].length > 200) errors.name = "Tên tối đa 200 ký tự";
  if ([...person.address.trim()].length > 300) errors.address = "Địa chỉ tối đa 300 ký tự";
  const number = normalizeIdNumber(person.idNumber);
  if (person.idKind === "" && number && !isMaskedIdNumber(number)) {
    errors.idNumber = "Chọn loại giấy tờ (CCCD hoặc hộ chiếu) cho số đã nhập";
  }
  if (person.idKind !== "" && !isMaskedIdNumber(number)) {
    if (person.idKind === "cccd" && !cccdPattern.test(number))
      errors.idNumber = "Số CCCD phải đúng 12 chữ số";
    if (person.idKind === "passport" && !passportPattern.test(number))
      errors.idNumber = "Số hộ chiếu có 6–20 chữ cái hoặc chữ số";
  }
  if (person.idKind === "passport" && !person.idNationality.trim()) {
    errors.idNationality = "Hộ chiếu cần ghi quốc tịch";
  }
  return errors;
}

export function personBuyerIsComplete(
  invoiceType: CustomerBookingInvoiceType,
  person: PersonBuyer,
): boolean {
  return Object.keys(validatePersonBuyer(invoiceType, person)).length === 0;
}

type PersonPayload = {
  invoiceBuyerKind?: "consumer" | "personal";
  invoicePersonName?: string;
  invoicePersonAddress?: string;
  invoicePersonIdKind?: PersonIdKind;
  invoicePersonIdNumber?: string;
  invoicePersonIdNationality?: string;
};

/**
 * Phần người mua cá nhân của payload. Chỉ có nghĩa với eShop; VAT (công ty) trả
 * object rỗng (máy chủ tự xoá cụm cá nhân khi đổi sang VAT). Ô nào không gửi thì
 * VẮNG hẳn khoá, không phải `undefined` (exactOptionalPropertyTypes). `withId =
 * false` cho trang đặt lịch công khai — không hỏi CCCD.
 */
export function personPayload(
  invoiceType: CustomerBookingInvoiceType,
  person: PersonBuyer,
  withId = true,
): PersonPayload {
  if (invoiceType !== "personal") return {};
  if (!person.provided) {
    return {
      invoiceBuyerKind: "consumer",
      invoicePersonName: "",
      invoicePersonAddress: "",
      ...(withId
        ? {
            invoicePersonIdKind: "" as const,
            invoicePersonIdNumber: "",
            invoicePersonIdNationality: "",
          }
        : {}),
    };
  }
  const idKind = withId ? person.idKind : "";
  return {
    invoiceBuyerKind: "personal",
    invoicePersonName: person.name.trim(),
    invoicePersonAddress: person.address.trim(),
    ...(withId
      ? {
          invoicePersonIdKind: idKind,
          invoicePersonIdNumber: idKind ? normalizeIdNumber(person.idNumber) : "",
          invoicePersonIdNationality: idKind === "passport" ? person.idNationality.trim() : "",
        }
      : {}),
  };
}

/** Người mua đọc từ một đơn / lịch đặt đã lưu. eShop cũ (ô trống) = không cung cấp. */
export function personFromStored(stored: {
  invoiceBuyerKind?: string | null | undefined;
  invoicePersonName?: string | null | undefined;
  invoicePersonAddress?: string | null | undefined;
  invoicePersonIdKind?: string | null | undefined;
  invoicePersonIdNumber?: string | null | undefined;
  invoicePersonIdNationality?: string | null | undefined;
}): PersonBuyer {
  if (stored.invoiceBuyerKind !== "personal") return emptyPersonBuyer;
  const kind = stored.invoicePersonIdKind;
  return {
    provided: true,
    name: stored.invoicePersonName ?? "",
    address: stored.invoicePersonAddress ?? "",
    idKind: kind === "cccd" || kind === "passport" ? kind : "",
    idNumber: stored.invoicePersonIdNumber ?? "",
    idNationality: stored.invoicePersonIdNationality ?? "",
  };
}
